/* eslint-disable max-len */
import { createMachine, spawn } from 'xstate';
import { assign } from '@xstate/immer';
import LocalStorageKeys from 'src/components/Authenticator/constants';
import * as NavigationMenu from './navigationMenu.types';
import { ROUTES } from 'src/router/routes';
import myUpdatesSM from '../myUpdates/myUpdates.machine';
// For debugging
// import { inspect } from '@xstate/inspect';
// inspect({ iframe: false });

export const navigationMenuSM = createMachine(
  {
    id: 'NavigationMenuSM',
    tsTypes: {} as import('./navigationMenu.machine.typegen').Typegen0,
    predictableActionArguments: true,
    preserveActionOrder: true,
    schema: {
      context: {} as NavigationMenu.SMContext,
      events: {} as NavigationMenu.SMEvents,
      services: {} as NavigationMenu.SMServices,
    },
    context: {
      collapsed: false,
      deviceMode: undefined!,
      allNavigationItemsConfig: undefined!,
      history: undefined!,
      myUpdatesActor: undefined,
      currentRoute: undefined,
    },
    initial: 'preparingMachine',
    states: {
      preparingMachine: {
        always: { target: 'ready', actions: 'setCollapsedState' },
      },
      ready: {
        initial: 'determine',
        states: {
          determine: {
            always: [
              { cond: 'isCurrentlyViewerDashboard', target: 'viewerDashboard' },
              { cond: 'isCurrentlyViewerLibrary', target: 'viewerLibrary' },
              { cond: 'isCurrentlyViewerFoldersMyFolders', target: 'viewerFolders.myFolders' },
              { cond: 'isCurrentlyViewerFoldersSharedWithMe', target: 'viewerFolders.sharedWithMe' },
              { cond: 'isCurrentlyViewerFoldersMyUploads', target: 'viewerFolders.myUploads' },
              { cond: 'isCurrentlyViewerFoldersMyUpdates', target: 'viewerFolders.myUpdates' },
              { cond: 'isCurrentlyViewerFoldersArchivedFolders', target: 'viewerFolders.archivedFolders' },
              { cond: 'isCurrentlyViewerFavoriteFiles', target: 'viewerFavorites.favoriteFiles' },
              { cond: 'isCurrentlyViewerFavoriteFolders', target: 'viewerFavorites.favoriteFolders' },
              { cond: 'isCurrentlyViewerMeetingHistory', target: 'viewerMeetingHistory' },
              { cond: 'isCurrentlyViewerNotifications', target: 'viewerNotifications' },
              { cond: 'isCurrentlyViewerSearch', target: 'viewerSearch' },
              { cond: 'isCurrentlyAccountSettings', target: 'accountSettings' },
              { cond: 'isCurrentlyViewerProfilePassword', target: 'viewerProfilePassword' },
              { cond: 'isCurrentlyViewerProfilBeaconApp', target: 'viewerProfileBeaconApp' },
              { cond: 'isCurrentlyViewerCRMIntegration', target: 'viewerCRMIntegration' },
              { cond: 'isCurrentlyViewerHubs', target: 'viewerHubs' },
              { cond: 'isCurrentlyViewerAnalytics', target: 'viewerAnalytics' },
              { cond: 'isCurrentlyPublisherLibrary', target: 'publisherLibrary' },
              { cond: 'isCurrentlyPublisherFoldersMyFolders', target: 'publisherFolders.myFolders' },
              { cond: 'isCurrentlyPublisherFoldersSharedWithMe', target: 'publisherFolders.sharedWithMe' },
              { cond: 'isCurrentlyPublisherFoldersMyUpdates', target: 'publisherFolders.myUpdates' },
              { cond: 'isCurrentlyPublisherFoldersArchivedFolders', target: 'publisherFolders.archivedFolders' },
              { cond: 'isCurrentlyPublisherEmailTemplates', target: 'publisherEmailTemplates' },
              { cond: 'isCurrentlyPublisherHubTemplates', target: 'publisherHubTemplates' },
              { cond: 'isCurrentlyPublisherAnalytics', target: 'publisherAnalytics' },
              { cond: 'isCurrentlyPublisherSearch', target: 'publisherSearch' },
              { cond: 'isCurrentlyPublisherAdmin', target: 'publisherAdmin' },
              { cond: 'isCurrentlyPublisherAdminUser', target: 'publisherAdmin.adminUser' },
              { cond: 'isCurrentlyPublisherAIBeacon', target: 'publisherAdmin.aiBeacon' },
              { cond: 'isCurrentlyPublisherAdminIntegration', target: 'publisherAdmin.adminIntegration' },
              { target: 'unsupported' },
            ],
          },
          viewerDashboard: {},
          viewerLibrary: {},
          viewerFolders: {
            initial: 'myFolders',
            states: {
              myFolders: {},
              sharedWithMe: {},
              myUploads: {},
              myUpdates: {
                after: { 0: { actions: 'spawnMyUpdatesActor' } },
                exit: ['destroyMyUpdatesActor'],
              },
              archivedFolders: {},
            },
          },
          viewerFavorites: {
            initial: 'favoriteFiles',
            states: {
              favoriteFiles: {},
              favoriteFolders: {},
            },
          },
          viewerMeetingHistory: {},
          viewerNotifications: {},
          viewerSearch: {},
          accountSettings: {},
          viewerProfilePassword: {},
          viewerProfileBeaconApp: {},
          viewerCRMIntegration: {},
          viewerHubs: {},
          viewerAnalytics: {},
          publisherLibrary: {},
          publisherFolders: {
            initial: 'myFolders',
            states: {
              myFolders: {},
              sharedWithMe: {},
              myUpdates: {
                after: { 0: { actions: 'spawnMyUpdatesActor' } },
                exit: ['destroyMyUpdatesActor'],
              },
              archivedFolders: {},
            },
          },
          publisherEmailTemplates: {},
          publisherHubTemplates: {},
          publisherAnalytics: {},
          publisherSearch: {},
          publisherAdmin: {
            initial: 'adminUser',
            states: {
              adminUser: {},
              aiBeacon: {},
              adminIntegration: {},
            },
          },
          unsupported: {},
        },
        on: {
          TOGGLE_COLLAPSE_STATE: {
            actions: 'toggleCollapseState',
          },
          UPDATE_CURRENT_ROUTE: [
            { cond: 'isGoingToViewerDashboard', actions: 'updateCurrentRoute', target: 'ready.viewerDashboard' },
            { cond: 'isGoingToViewerLibrary', actions: 'updateCurrentRoute', target: 'ready.viewerLibrary' },
            { cond: 'isGoingToViewerFoldersMyFolders', actions: 'updateCurrentRoute', target: 'ready.viewerFolders.myFolders' },
            { cond: 'isGoingToViewerFoldersSharedWithMe', actions: 'updateCurrentRoute', target: 'ready.viewerFolders.sharedWithMe' },
            { cond: 'isGoingToViewerFoldersMyUploads', actions: 'updateCurrentRoute', target: 'ready.viewerFolders.myUploads' },
            { cond: 'isGoingToViewerFoldersMyUpdates', actions: 'updateCurrentRoute', target: 'ready.viewerFolders.myUpdates' },
            { cond: 'isGoingToViewerFoldersArchivedFolders', actions: 'updateCurrentRoute', target: 'ready.viewerFolders.archivedFolders' },
            { cond: 'isGoingToViewerFavoriteFiles', actions: 'updateCurrentRoute', target: 'ready.viewerFavorites.favoriteFiles' },
            { cond: 'isGoingToViewerFavoriteFolders', actions: 'updateCurrentRoute', target: 'ready.viewerFavorites.favoriteFolders' },
            { cond: 'isGoingToViewerMeetingHistory', actions: 'updateCurrentRoute', target: 'ready.viewerMeetingHistory' },
            { cond: 'isGoingToViewerNotifications', actions: 'updateCurrentRoute', target: 'ready.viewerNotifications' },
            { cond: 'isGoingToViewerSearch', actions: 'updateCurrentRoute', target: 'ready.viewerSearch' },
            { cond: 'isGoingToAccountSettings', actions: 'updateCurrentRoute', target: 'ready.accountSettings' },
            { cond: 'isGoingToViewerProfilePassword', actions: 'updateCurrentRoute', target: 'ready.viewerProfilePassword' },
            { cond: 'isGoingToViewerProfileBeaconApp', actions: 'updateCurrentRoute', target: 'ready.viewerProfileBeaconApp' },
            { cond: 'isGoingToViewerCRMIntegration', actions: 'updateCurrentRoute', target: 'ready.viewerCRMIntegration' },
            { cond: 'isGoingToViewerHubs', actions: 'updateCurrentRoute', target: 'ready.viewerHubs' },
            { cond: 'isGoingToViewerAnalytics', actions: 'updateCurrentRoute', target: 'ready.viewerAnalytics' },
            { cond: 'isGoingToPublisherLibrary', actions: 'updateCurrentRoute', target: 'ready.publisherLibrary' },
            { cond: 'isGoingToPublisherFoldersMyFolders', actions: 'updateCurrentRoute', target: 'ready.publisherFolders.myFolders' },
            { cond: 'isGoingToPublisherFoldersSharedWithMe', actions: 'updateCurrentRoute', target: 'ready.publisherFolders.sharedWithMe' },
            { cond: 'isGoingToPublisherFoldersMyUpdates', actions: 'updateCurrentRoute', target: 'ready.publisherFolders.myUpdates' },
            { cond: 'isGoingToPublisherFoldersArchivedFolders', actions: 'updateCurrentRoute', target: 'ready.publisherFolders.archivedFolders' },
            { cond: 'isGoingToPublisherEmailTemplates', actions: 'updateCurrentRoute', target: 'ready.publisherEmailTemplates' },
            { cond: 'isGoingToPublisherHubTemplates', actions: 'updateCurrentRoute', target: 'ready.publisherHubTemplates' },
            { cond: 'isGoingToPublisherAnalytics', actions: 'updateCurrentRoute', target: 'ready.publisherAnalytics' },
            { cond: 'isGoingToPublisherSearch', actions: 'updateCurrentRoute', target: 'ready.publisherSearch' },
            { cond: 'isGoingToPublisherAdmin', actions: 'updateCurrentRoute', target: 'ready.publisherAdmin' },
            { cond: 'isGoingToPublisherAdminUser', actions: 'updateCurrentRoute', target: 'ready.publisherAdmin.adminUser' },
            { cond: 'isGoingToPublisherAIBeacon', actions: 'updateCurrentRoute', target: 'ready.publisherAdmin.aiBeacon' },
            { cond: 'isGoingToPublisherAdminIntegration', actions: 'updateCurrentRoute', target: 'ready.publisherAdmin.adminIntegration' },
            { actions: 'updateCurrentRoute', target: 'ready.unsupported' },
          ],
          UPDATE_ALL_NAVIGATION_ITEMS_CONFIG: {
            actions: 'updateAllNavigationItemsConfig',
          },
        },
      },
    },
  },
  {
    guards: {
      isCurrentlyViewerDashboard: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_DASHBOARD.PATH,
      isCurrentlyViewerLibrary: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_LIBRARY.PATH,
      isCurrentlyViewerFoldersMyFolders: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_FOLDERS_MY_FOLDERS.PATH,
      isCurrentlyViewerFoldersSharedWithMe: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_FOLDERS_SHARED_WITH_ME.PATH,
      isCurrentlyViewerFoldersMyUploads: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_FOLDERS_MY_UPLOADS.PATH,
      isCurrentlyViewerFoldersMyUpdates: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_FOLDERS_MY_UPDATES.PATH,
      isCurrentlyViewerFoldersArchivedFolders: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_FOLDERS_ARCHIVED.PATH,
      isCurrentlyViewerFavoriteFiles: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_FAVORITE_FILES?.PATH,
      isCurrentlyViewerFavoriteFolders: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_FAVORITE_FOLDERS?.PATH,
      isCurrentlyViewerMeetingHistory: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_MEETING_HISTORY.PATH,
      isCurrentlyViewerNotifications: (ctx) =>
        ctx.currentRoute?.PATH === ROUTES.UI2_NOTIFICATIONS.PATH ||
        ctx.currentRoute?.PATH === ROUTES.BULLETIN.PATH,
      isCurrentlyViewerSearch: (ctx) => ctx.currentRoute?.PATH === ROUTES.SEARCH_RESULTS.PATH,
      isCurrentlyAccountSettings: (ctx) => ctx.currentRoute?.PATH === ROUTES.PROFILE.PATH,
      isCurrentlyViewerProfilePassword: (ctx) => ctx.currentRoute?.PATH === ROUTES.PROFILE_PASSWORD.PATH,
      isCurrentlyViewerProfilBeaconApp: (ctx) => ctx.currentRoute?.PATH === ROUTES.PROFILE_OFFLINE.PATH,
      isCurrentlyViewerCRMIntegration: (ctx) => ctx.currentRoute?.PATH === ROUTES.CRM_INTEGRATION.PATH,
      isCurrentlyViewerHubs: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_HUBS.PATH,
      isCurrentlyViewerAnalytics: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_ANALYTICS.PATH,
      isCurrentlyPublisherLibrary: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_PUBLISHER_LIBRARY.PATH,
      isCurrentlyPublisherFoldersMyFolders: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_PUBLISHER_FOLDERS_MY_FOLDERS.PATH,
      isCurrentlyPublisherFoldersSharedWithMe: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_PUBLISHER_FOLDERS_SHARED_WITH_ME.PATH,
      isCurrentlyPublisherFoldersMyUpdates: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_PUBLISHER_FOLDERS_MY_UPDATES.PATH,
      isCurrentlyPublisherFoldersArchivedFolders: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_PUBLISHER_FOLDERS_ARCHIVED.PATH,
      isCurrentlyPublisherEmailTemplates: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_PUBLISHER_EMAIL_TEMPLATES.PATH,
      isCurrentlyPublisherHubTemplates: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_PUBLISHER_HUB_TEMPLATES.PATH,
      isCurrentlyPublisherAnalytics: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_PUBLISHER_ANALYTICS.PATH,
      isCurrentlyPublisherSearch: (ctx) => ctx.currentRoute?.PATH === ROUTES.SEARCH_RESULTS_PUBLISHER.PATH,
      isCurrentlyPublisherAIBeacon: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_PUBLISHER_AI_BEACON.PATH,
      isCurrentlyPublisherAdmin: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_PUBLISHER_ADMIN.PATH,
      isCurrentlyPublisherAdminUser: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_PUBLISHER_ADMIN_USER.PATH,
      isCurrentlyPublisherAdminIntegration: (ctx) => ctx.currentRoute?.PATH === ROUTES.UI2_PUBLISHER_ADMIN_INTEGRATION.PATH,
      isGoingToViewerDashboard: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_DASHBOARD.PATH,
      isGoingToViewerLibrary: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_LIBRARY.PATH,
      isGoingToViewerFoldersMyFolders: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_FOLDERS_MY_FOLDERS.PATH,
      isGoingToViewerFoldersSharedWithMe: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_FOLDERS_SHARED_WITH_ME.PATH,
      isGoingToViewerFoldersMyUploads: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_FOLDERS_MY_UPLOADS.PATH,
      isGoingToViewerFoldersMyUpdates: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_FOLDERS_MY_UPDATES.PATH,
      isGoingToViewerFoldersArchivedFolders: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_FOLDERS_ARCHIVED.PATH,
      isGoingToViewerFavoriteFiles: (_ctx, evt) => (evt as NavigationMenu.EVT_UPDATE_CURRENT_ROUTE).payload?.PATH === ROUTES.UI2_FAVORITE_FILES?.PATH,
      isGoingToViewerFavoriteFolders: (_ctx, evt) => (evt as NavigationMenu.EVT_UPDATE_CURRENT_ROUTE).payload?.PATH === ROUTES.UI2_FAVORITE_FOLDERS?.PATH,
      isGoingToViewerMeetingHistory: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_MEETING_HISTORY.PATH,
      isGoingToViewerNotifications: (_ctx, evt) =>
        evt.payload?.PATH === ROUTES.UI2_NOTIFICATIONS.PATH ||
        evt.payload?.PATH === ROUTES.BULLETIN.PATH,
      isGoingToViewerSearch: (_ctx, evt) => evt.payload?.PATH === ROUTES.SEARCH_RESULTS.PATH,
      isGoingToAccountSettings: (_ctx, evt) => evt.payload?.PATH === ROUTES.PROFILE.PATH,
      isGoingToViewerProfilePassword: (_ctx, evt) => evt.payload?.PATH === ROUTES.PROFILE_PASSWORD.PATH,
      isGoingToViewerProfileBeaconApp: (_ctx, evt) => evt.payload?.PATH === ROUTES.PROFILE_OFFLINE.PATH,
      isGoingToViewerCRMIntegration: (_ctx, evt) => evt.payload?.PATH === ROUTES.CRM_INTEGRATION.PATH,
      isGoingToViewerHubs: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_HUBS.PATH,
      isGoingToViewerAnalytics: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_ANALYTICS.PATH,
      isGoingToPublisherLibrary: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_PUBLISHER_LIBRARY.PATH,
      isGoingToPublisherFoldersMyFolders: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_PUBLISHER_FOLDERS_MY_FOLDERS.PATH,
      isGoingToPublisherFoldersSharedWithMe: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_PUBLISHER_FOLDERS_SHARED_WITH_ME.PATH,
      isGoingToPublisherFoldersMyUpdates: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_PUBLISHER_FOLDERS_MY_UPDATES.PATH,
      isGoingToPublisherFoldersArchivedFolders: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_PUBLISHER_FOLDERS_ARCHIVED.PATH,
      isGoingToPublisherEmailTemplates: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_PUBLISHER_EMAIL_TEMPLATES.PATH,
      isGoingToPublisherHubTemplates: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_PUBLISHER_HUB_TEMPLATES.PATH,
      isGoingToPublisherAnalytics: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_PUBLISHER_ANALYTICS.PATH,
      isGoingToPublisherSearch: (_ctx, evt) => evt.payload?.PATH === ROUTES.SEARCH_RESULTS_PUBLISHER.PATH,
      isGoingToPublisherAIBeacon: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_PUBLISHER_AI_BEACON.PATH,
      isGoingToPublisherAdmin: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_PUBLISHER_ADMIN.PATH,
      isGoingToPublisherAdminUser: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_PUBLISHER_ADMIN_USER.PATH,
      isGoingToPublisherAdminIntegration: (_ctx, evt) => evt.payload?.PATH === ROUTES.UI2_PUBLISHER_ADMIN_INTEGRATION.PATH,
    },
    actions: {
      setCollapsedState: assign((ctx) => {
        const storedValue = localStorage.getItem(LocalStorageKeys.BEACON_NAVIGATION_MENU_COLLAPSED);
        ctx.collapsed = storedValue ? JSON.parse(storedValue) : false;
      }),
      toggleCollapseState: assign((ctx) => {
        ctx.collapsed = !ctx.collapsed;
        localStorage.setItem(LocalStorageKeys.BEACON_NAVIGATION_MENU_COLLAPSED, JSON.stringify(ctx.collapsed));
      }),
      updateCurrentRoute: assign((ctx, evt) => {
        ctx.currentRoute = evt.payload;
      }),
      updateAllNavigationItemsConfig: assign((ctx, evt) => {
        ctx.allNavigationItemsConfig = evt.payload;
      }),
      spawnMyUpdatesActor: assign((ctx) => {
        if (!ctx.myUpdatesActor) {
          ctx.myUpdatesActor = spawn(
            myUpdatesSM.withContext({
              allExpanded: false,
            }),
            'MyUpdatesActor',
          );
        }
      }),
      destroyMyUpdatesActor: assign((ctx) => {
        if (ctx.myUpdatesActor && typeof ctx.myUpdatesActor.stop === 'function') {
          ctx.myUpdatesActor.stop();
        }
        ctx.myUpdatesActor = undefined;
      }),
    },
    services: {},
  },
)

export default navigationMenuSM
